import fb from '@/firebaseConfig';
import router from '@/router';
import analytics from '@/utils/Analytics';
import localStore from '@/utils/LocalStore';
import { getUrlParams } from '@/helpers/urlHelpers';
import { Plugins } from '@capacitor/core';

const { AuthPlugin } = Plugins;

let loadingTime = -500; // Time loading after sign up/sign in, until user data is loaded

/* See Auth-View for notes on Auth flows & screens

  Firebase auth notes:
  - Can't add a password if already signed in with link
  - If had PW first & email verified, can sign in with either password or link
  - If had PW first & email NOT verified, signing in with link removes password method
  - Clicking on verify email link goes to blank page with Firebase UI 
    that says "Email verified. You can now sign in to your new account"
*/

// Sign in with Gmail, then try to create account with password
// Error creating account with password {"code":"auth/email-already-in-use","message":"The email address is already in use by another account."}

// Sign in with password (email not verified), then sign in with Google
// Password method removed, can only sign in with Google
// If you then try to create a password, get error:
// {"code":"auth/email-already-in-use","message":"The email address is already in use by another account."}
/*

  STATE

 */

const state = {
  isSignedIn: false,
  screen: 'intro', // Current auth screen
  pendingEmail: '', // Email user is trying to sign in with
  redirectAfterSignIn: '',
  signInMethod: '', // or 'password'
  sendingEmail: false,
  signingInWithPassword: false,
  actionParams: {},
  error: null, // format: { code, message }
};

/*

  GETTERS

 */

const getters = {
  actionCodeSettings: (state, getters, rootState) => {
    // const { urlOrigin } = getters;
    const { isDevProject } = fb;
    const { isNativeApp } = rootState.device;

    if (isNativeApp) {
      return {
        // URL you want to redirect back to
        url: `https://${isDevProject ? 'dev.' : ''}app.imaginarie.app`,
        // Currently auth links not opening app in Android as of Nov 2020
        // Should change back when issue is fixed
        handleCodeInApp: true,
        // handleCodeInApp: false,
        iOS: {
          bundleId: isDevProject ? 'app.imaginarie.app.dev' : 'app.imaginarie.app',
        },
        android: {
          packageName: isDevProject ? 'app.imaginarie.app.dev' : 'app.imaginarie.app',
          installApp: true,
          minimumVersion: '23',
        },
        dynamicLinkDomain: isDevProject ? 'dev.link.imaginarie.app' : 'link.imaginarie.app',
      };
    }

    return { url: window.location.href, handleCodeInApp: true };
  },
};

/*

  ACTIONS

 */

const actions = {
  init({ commit, dispatch }) {
    fb.auth().onAuthStateChanged(user => {
      if (user) {
        commit('setSignedIn');
        dispatch('user/init', user, { root: true });
        analytics.setUserId(user.uid);
      } else {
        console.log('No user authenticated');
        commit('setSignedOut');
        commit('resetState');
        commit('user/clearUser', null, { root: true });
        analytics.setUserId();
      }
    });
  },

  // Determines which screen/step shows in auth flow
  // See Auth-View for details
  changeScreen({ commit }, screen) {
    commit('setScreen', screen);
    analytics.setCurrentScreen('Auth', screen);
  },

  /** SIGN IN WITH GOOGLE & APPPLE */

  async signInWithGoogle({ dispatch, commit, rootState }) {
    const { isNativeApp } = rootState.device;
    const { isAnonymous } = rootState.user;
    commit('setError', null);
    commit('setSignInMethod', 'google');
    commit('setSigningInWithPassword', false);
    const provider = new fb.auth.GoogleAuthProvider();

    if (isNativeApp) {
      try {
        const { idToken } = await AuthPlugin.signIn({ providerId: 'google.com' });
        const credential = fb.auth.GoogleAuthProvider.credential(idToken);

        let authResult;
        if (isAnonymous) {
          authResult = await fb.auth().currentUser.linkWithCredential(credential);
        } else {
          authResult = await fb.auth().signInWithCredential(credential);
        }
        dispatch('handleSignInSuccess', authResult);
      } catch (error) {
        const { code } = error;
        commit('setError', error);
        analytics.exception(code || 'Error signing in with Google on Native');
        console.error('Error signing in with Google on Native', JSON.stringify(error));
      }
      // Start redirect, finish with handleAuthRedirect
    } else if (isAnonymous) {
      fb.auth().currentUser.linkWithRedirect(provider);
    } else {
      fb.auth().signInWithRedirect(provider);
    }
  },

  async signInWithApple({ dispatch, commit, rootState }) {
    const { isNativeApp } = rootState.device;
    const { isAnonymous } = rootState.user;

    commit('setError', null);
    commit('setSignInMethod', 'apple');
    commit('setSigningInWithPassword', false);
    const provider = new fb.auth.OAuthProvider('apple.com');

    try {
      if (isNativeApp) {
        const { idToken, rawNonce = '' } = await AuthPlugin.signIn({ providerId: 'apple.com' });
        const credential = provider.credential(rawNonce ? { idToken, rawNonce } : { idToken });

        let authResult;
        if (isAnonymous) {
          authResult = await fb.auth().currentUser.linkWithCredential(credential);
        } else {
          authResult = await fb.auth().signInWithCredential(credential);
        }
        dispatch('handleSignInSuccess', authResult);
        // Start redirect, finish with handleAuthRedirect
      } else if (isAnonymous) {
        fb.auth().currentUser.linkWithRedirect(provider);
      } else {
        fb.auth().signInWithRedirect(provider);
      }
    } catch (error) {
      const { code } = error;
      commit('setError', error);
      analytics.exception(code || 'Error signing in with Apple ');
      console.error('Error signing in with Apple ', JSON.stringify(error));
    }
  },

  // Called from Auth-View after signing in with Google or Apple on web
  // Also called every time the app opens Auth-View to check for redirects
  // If redirect exists & user is authenticated, continue with sign-in
  async handleAuthRedirect({ dispatch, commit }) {
    // console.log('handleAuthRedirect');
    try {
      const authResult = await fb.auth().getRedirectResult();
      if (authResult.user) {
        dispatch('handleSignInSuccess', authResult);
      }
    } catch (error) {
      const { code } = error;
      // This error happens on iOS when there's no auth redirect, so disabling this error
      if (error.code !== 'auth/operation-not-supported-in-this-environment') {
        commit('setError', error);
        analytics.exception(code || 'Error signing in from redirect on Web');
        console.error('Error signing in from redirect on Web', JSON.stringify(error));
      }
    }
  },

  /** SIGN IN WITH PASSWORD */

  // For password sign-in for new or returning users
  // Dispatched after user enters email address on password auth screen
  // Check whether account exists, and go to enter or create password screens, or show erro
  async handleEnterEmail({ commit, dispatch }, email) {
    commit('setPendingEmail', email);
    commit('setError', null);

    const signInMethodsData = await fb.getSignInMethods(email);
    const { hasPassword, hasGoogle, hasApple } = signInMethodsData;

    if (hasPassword) {
      commit('setSignInMethod', 'password');
      dispatch('changeScreen', 'enter-password');
    } else if (!hasPassword && (hasGoogle || hasApple)) {
      commit('setError', {
        code: 'auth/email-already-in-use',
        message: 'The email address is already in use by another account.',
      });
    } else {
      commit('setSignInMethod', 'password');
      dispatch('changeScreen', 'create-password');
    }
  },

  saveRedirect(context, redirect) {
    localStore.setItem('redirectAfterSignIn', redirect);
  },

  async createUserWithPassword({ commit, dispatch, rootState }, { email, password }) {
    const { isAnonymous } = rootState.user;
    commit('setError', null);
    commit('setSignInMethod', 'password');
    commit('setSigningInWithPassword', true);
    commit('setPendingEmail', email);

    try {
      let authResult;
      if (isAnonymous) {
        const credential = fb.auth.EmailAuthProvider.credential(email, password);
        authResult = await fb.auth().currentUser.linkWithCredential(credential);
      } else {
        authResult = await fb.auth().createUserWithEmailAndPassword(email, password);
        dispatch('sendEmailVerification');
      }
      commit('setSigningInWithPassword', false);
      dispatch('handleSignInSuccess', authResult);
    } catch (error) {
      commit('setSigningInWithPassword', false);
      commit('setError', error);
      console.error('Error creating account with password', JSON.stringify(error));
    }
  },

  async signInWithPassword({ commit, dispatch, rootState }, { email = '', password = '' }) {
    const { isAnonymous } = rootState.user;
    commit('setError', null);
    commit('setSignInMethod', 'password');
    commit('setSigningInWithPassword', true);
    commit('setPendingEmail', email);

    try {
      let authResult;
      if (isAnonymous) {
        const credential = fb.auth.EmailAuthProvider.credential(email, password);
        authResult = await fb.auth().currentUser.linkWithCredential(credential);
      } else {
        authResult = await fb.auth().signInWithEmailAndPassword(email, password);
      }
      commit('setSigningInWithPassword', false);
      dispatch('handleSignInSuccess', authResult);
    } catch (error) {
      commit('setError', error);
      commit('setSigningInWithPassword', false);
      analytics.exception((error && error.code) || 'Error signing in with password');
      console.error('Error signing in with password', JSON.stringify(error));
    }
  },

  saveNewPassword({ commit, dispatch, state }, newPassword) {
    const { oobCode: actionCode } = state.actionParams;
    const { pendingEmail } = state;

    commit('setSigningInWithPassword', true);

    fb.auth()
      .confirmPasswordReset(actionCode, newPassword)
      .then(() => {
        dispatch('signInWithPassword', { email: pendingEmail, password: newPassword });
      })
      .catch(error => {
        commit('setError', error);
        analytics.exception((error && error.code) || 'Error resetting password');
        console.error('Error resetting password', error);
      });
  },

  /** SIGN-IN ANONYMOUSLY */

  signInAnonymously({ commit, dispatch }) {
    commit('setError', null);
    commit('setSignInMethod', 'anonymous');
    commit('setSigningInWithPassword', false);
    commit('setPendingEmail', '');

    fb.auth()
      .signInAnonymously()
      .then(authResult => {
        dispatch('handleSignInSuccess', authResult);
      })
      .catch(error => {
        commit('setError', error);
        commit('setSigningInWithPassword', false);
        analytics.exception((error && error.code) || 'Error signing in anonymously');
        console.error('Error signing in with password', JSON.stringify(error));
      });
  },

  /** HANDLE SUCCESSFUL SIGN IN */

  async handleSignInSuccess({ commit, dispatch, state }, authResult) {
    commit('setError', null);
    // console.log('authResult', authResult);

    const {
      user: { uid, email, emailVerified, displayName, isAnonymous },
      additionalUserInfo: { isNewUser },
      operationType,
    } = authResult;
    const { signInMethod } = state;
    // console.log('handleSignInSuccess', { uid, isNewUser, operationType });

    // Show loading screen while checking if user data is loaded
    dispatch('changeScreen', isNewUser ? 'creating-account' : 'signing-in');
    dispatch('redirectAfterSignInSuccess', isNewUser);

    // When upgrading anonymous users, up date DB with basic user info
    if (operationType === 'link') {
      dispatch(
        'user/saveUserInitDataToDB',
        {
          moduleName: 'user',
          moduleData: { email, emailVerified, displayName, isAnonymous },
        },
        { root: true }
      );
      commit('user/setUidEmail', authResult.user, { root: true });
      analytics.userUpgradedAnonymous(signInMethod);
      const addEmailContactForUpgrade = fb.functions.httpsCallable(
        'email-addEmailContactForUpgrade'
      );
      addEmailContactForUpgrade({ email });
    }

    analytics.userSignedIn(uid, isNewUser, signInMethod);
  },

  async redirectAfterSignInSuccess({ dispatch, rootState }, isNewUser) {
    const { userDataLoaded } = rootState.user;
    loadingTime += 500;
    const minTime = isNewUser ? 1999 : 999;
    const maxTime = isNewUser ? 4999 : 2999;

    if (loadingTime < minTime || (loadingTime < maxTime && !userDataLoaded)) {
      setTimeout(() => {
        dispatch('redirectAfterSignInSuccess', isNewUser);
      }, 500);
    } else {
      const redirect = await localStore.getItem('redirectAfterSignIn');
      localStore.removeItem('redirectAfterSignIn');
      if (redirect) {
        router.replace(redirect);
      } else {
        router.replace('/');
      }
    }
  },

  /** PASSWORD RESET & EMAIL VERIFICATION */

  async sendPasswordResetLink({ commit, dispatch, getters }, email = '') {
    commit('setSignInMethod', 'password');
    commit('setSendingEmail', true);
    commit('setPendingEmail', email);
    commit('setError', null);

    const { actionCodeSettings } = getters;

    try {
      const sendPasswordResetEmail = fb.functions.httpsCallable('auth-sendPasswordResetEmail');
      await sendPasswordResetEmail({
        email,
        actionCodeSettings,
      });
      commit('setSendingEmail', false);
      dispatch('changeScreen', 'check-email');
    } catch (error) {
      commit('setSendingEmail', false);
      commit('setError', error);
      dispatch('changeScreen', 'error-sending-link');
      analytics.exception((error && error.code) || 'Error sending password recovery link');
      console.error('Error sending password recovery link', JSON.stringify(error));
    }
  },

  // If user can't find email, can resend password recovery
  resendEmail({ dispatch, state }) {
    const { pendingEmail } = state;
    if (pendingEmail) {
      dispatch('sendPasswordResetLink', pendingEmail);
    } else {
      dispatch('changeScreen', 'forgot-password');
    }
  },

  // Sent to password users after they sign in
  async sendEmailVerification({ dispatch, getters }, showSnack) {
    const { email } = fb.auth().currentUser || {};
    const { actionCodeSettings } = getters;

    try {
      const sendEmailVerification = fb.functions.httpsCallable('auth-sendEmailVerification');
      await sendEmailVerification({
        email,
        actionCodeSettings,
      });
      if (showSnack) {
        dispatch(
          'ui/updateSnack',
          { text: 'Verification email sent', quick: true },
          { root: true }
        );
      }
    } catch (error) {
      if (showSnack) {
        dispatch(
          'ui/updateSnack',
          { text: 'Error sending verification email. Try again later' },
          { root: true }
        );
      }
      analytics.exception((error && error.code) || 'Error sending email verification');
      console.error('Error sending email verification', error);
    }
  },

  // IMPORTANT: Auth actions like reset password and verify email use the Action URL
  // which is the LIVE page at [dev.]app.imaginarie.app
  // There's no localhost equivalent (asfaik)
  handleAuthActionLink({ state, commit, dispatch }, url) {
    dispatch('changeScreen', 'loading');
    const params = getUrlParams(url);
    commit('setActionParams', params);
    const { mode } = params;

    if (state.isSignedIn) router.replace({ name: 'AuthUpdate' });
    if (mode === 'resetPassword') {
      dispatch('handleResetPasswordLink');
    } else if (mode === 'verifyEmail') {
      dispatch('handleVerifyEmailLink');
    } else {
      console.error('Invalid mode for auth action link');
    }
    // mode === 'recoverEmail'
    // Not using unless we give options to change email address
    // This would allow a user to cancel email address change
  },

  handleResetPasswordLink({ commit, dispatch, state }) {
    commit('setSignInMethod', 'password');
    commit('setError', null);

    const { oobCode: actionCode } = state.actionParams;

    fb.auth()
      .verifyPasswordResetCode(actionCode)
      .then(email => {
        commit('setPendingEmail', email);
        dispatch('changeScreen', 'reset-password');
      })
      .catch(error => {
        commit('setError', error);
        dispatch('changeScreen', 'error-expired-link');
        analytics.exception((error && error.code) || 'Error with reset password link');
      });
  },

  handleVerifyEmailLink({ commit, dispatch }) {
    const { oobCode: actionCode } = state.actionParams;

    fb.auth()
      .applyActionCode(actionCode)
      .then(() => {
        dispatch('changeScreen', 'email-verified');
      })
      .catch(error => {
        // Code is invalid or expired
        commit('setError', error);
        dispatch('changeScreen', 'error-verification-link');
        analytics.exception((error && error.code) || 'Error with verification email');
      });
  },

  /** REAUTHENTICATE */

  // TODO Reauthenticate function
  // Currently just sending to sign in page, need to test
  // that can use sign in instead of reauthenticationwithCredential

  /** SIGN OUT & DELETE ACCOUNT */

  async signOut({ dispatch, rootState }) {
    const { isNativeApp } = rootState.device;
    await fb.auth().signOut();
    dispatch('purchases/reset', null, { root: true });
    dispatch('cards/clearRecentIds', null, { root: true });
    dispatch('stacks/clearRecentIds', null, { root: true });
    if (isNativeApp) {
      AuthPlugin.signOut();
    }
    analytics.userSignedOut();
    window.location.reload();
    // dispatch('cards/unsubscribeToCardSnapshots', null, { root: true });
    console.log('Signed out');
  },

  async deleteAccount({ dispatch, commit }) {
    try {
      await fb.auth().currentUser.delete();
      dispatch('purchases/reset', null, { root: true });
      dispatch('cards/clearRecentIds', null, { root: true });
      dispatch('stacks/clearRecentIds', null, { root: true });
      analytics.userDeletedAccount();
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      console.log('Account deleted');
    } catch (error) {
      commit('setError', error);
      console.error('Error deleting account', error);
    }
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setSignedIn(state) {
    state.isSignedIn = true;
    state.error = null;
  },

  setScreen(state, screen) {
    state.screen = screen;
  },

  setPendingEmail(state, email) {
    state.pendingEmail = email;
  },

  setSignInMethod(state, method) {
    state.signInMethod = method;
  },

  setSendingEmail(state, sendingEmail) {
    state.sendingEmail = sendingEmail;
  },

  setSigningInWithPassword(state, signingInWithPassword) {
    state.signingInWithPassword = signingInWithPassword;
  },

  setActionParams(state, params) {
    state.actionParams = params;
  },

  // Called throughout actions, also called from AuthForms/cancelPasswordSignIn
  setError(state, error = null) {
    state.error = error;
  },

  // Called when user signs out, and from Auth-View destroyed method
  resetState(state) {
    state.screen = 'intro';
    state.pendingEmail = '';
    state.emailLinkUrl = '';
    state.redirectAfterSignIn = '';
    state.signInMethod = '';
    state.sendingEmail = false;
    state.signingInWithPassword = false;
    state.actionParams = {};
    state.error = null;
  },

  setSignedOut(state) {
    state.isSignedIn = false;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
